/**
 * event-identity — the one way an event names itself to a buyer, mounted by
 * every storefront surface that has to say WHICH event this is: the event
 * page above its ticket types, order-access above the ticket itself. One
 * component rather than one masthead per route, so the same facts never
 * arrive in two type scales with two date formats — the defect this replaces.
 *
 * The order is the ticket's own: headline (what) → dateline (when) → locator
 * (where), the exact order @momo/ticket-template's editorial and strata
 * cards use, so the page a buyer reads and the artifact they keep speak one
 * typographic language. The title's own size comes from the column it sits in
 * rather than from a per-host dose: `cqi` already reads the only input that
 * differs between hosts, so the component takes no size property at all and
 * the typography renders `token=""`. A rung attribute beside a full font-size
 * override names a size nothing renders — the primitive says so itself, and
 * the rung contributes nothing else: headline weight comes from a
 * token-independent rule, the primitive declares no letter-spacing, and
 * `leading-tight` on the same element outranks the rung's own line-height.
 *
 * Spacing says what belongs together: 4px inside the eyebrow+headline group,
 * 12px between groups, and the address lines stack flush against the venue
 * because the locator is one paragraph, not two. Prominence descends by size
 * first — the description sits at body/md on the default on-surface ink
 * because it is the subtitle, and only the address, the least of the facts,
 * dims to onVariant. The venue keeps full ink: it is the anchor text of the
 * map link, and a control does not dim to signal that it is metadata.
 *
 * The title holds one line at every column this product ships. That column is
 * 476px on a desktop — the page caps at 672px, the header insets `px-6` and
 * sets a `w-32` poster across a `gap-5` — 199px at a 375px phone and 144px at
 * the narrowest viewport, which is 45% of the viewport on a phone against 37%
 * on a desktop. That ratio is why the headline family's own ramp cannot do
 * this job: it interpolates on `100vw`, and no viewport-keyed ramp expresses
 * "fit this column". So the column declares `container-type: inline-size` and
 * the title interpolates on `cqi` instead — `clamp(20px, calc(14.8px +
 * 3.61cqi), 32px)`, whose endpoints are both rungs of the headline family:
 * 32px at a 476px column is `headline/lg`, and 20px at 144px is
 * `headline/xs`, below which the title falls to ≤1.29× the description beside it
 * and reads as a subtitle rather than as the page's subject. The container
 * sits on the column wrapper rather than on `:host`, because `container-type`
 * implies `contain: inline-size` and the host's own inline size is its
 * mount's to decide. That containment is also a constraint on where this
 * component may be mounted: the column now contributes zero max-content, so an
 * `event-identity` inside a shrink-to-fit parent collapses. Both mounts give
 * it a definite inline size — `flex-1` in the header, a fixed frame in the
 * studio — so nothing collapses today, and a shrink-to-fit host would.
 *
 * Fitting is not the same as holding one line. At a 199px column even 20px
 * carries about 16 characters of Poppins and the shortest real name in the
 * corpus past that is 24, so on every phone a real name is shortened, and the
 * shortening — not the ramp — is what holds the line there. The mark is
 * printed: `text-overflow: ellipsis`. A name is not an excerpt, so the
 * description's own argument below does not reach it — a name cut without a mark
 * reads as a shorter name, and the reader has no way to know they are not
 * reading the event's actual name, where nobody ever believes they have read
 * a whole description. That mark is also the door: where the cut bit, the title
 * sits inside a real `<button>` opening the same overlay the description opens, and
 * what the eye was denied is read there in full — `text-overflow` is purely
 * visual and never touches the accessible text, so the label leads with the
 * whole name and the door only has to say what pressing does. No chevron, no
 * icon, and no underline: the product marks a link on approach rather than at
 * rest, and a resting rule under a 20–32px headline would read as a link two
 * rows above the venue line that actually is one. The ellipsis is the
 * affordance.
 *
 * The description is bounded at three lines whatever its length. Nothing caps
 * it upstream — `Event.EventSchema` declares the field with no maximum — so an
 * 822-character description otherwise outweighs the name it belongs to and pushes
 * the ticket types off a phone's first screen. The bound is a max-height and a
 * mask, never `-webkit-line-clamp`, which prints its own ellipsis: an ellipsis
 * announces that text was taken away, and this trims to spend the column on
 * the event rather than to declare a limit. The last line dissolves into the
 * surface instead — no glyph, no rule, nothing added to the column — so the
 * trimmed edge reads as typographic rather than mechanical. That trimmed block
 * is itself the door: a real `<button>`, so role, tab stop, Enter/Space and
 * the accessible name come from the platform rather than from ARIA laid over a
 * div, and it opens the full text in an overlay honouring the author's own
 * newlines, which three clamped lines have to collapse to be worth three
 * lines. Either affordance renders only where its own cut actually bit,
 * measured on the laid-out box of this instance — a name and a description that fit
 * render exactly what they rendered before there were bounds, with nothing to
 * press and nothing dimmed.
 *
 * Schedule formatting is @mhmo91/rx's `formatDateRange` — the same call the
 * landing grid, the events console, and the event form already make, in the
 * venue's own zone so a Berlin 20:00 reads 20:00 from any browser. A run
 * that stays inside one month drops what its two ends share ("5.–13.
 * September 2026"); a run that crosses months has nothing to drop, so it
 * takes the short month name to stay on one line ("5. Sept. – 13. Okt.
 * 2026").
 *
 * No glyph beside the date: an icon set beside the very words it would
 * otherwise replace earns nothing the words don't already carry. The venue
 * carries a directions glyph because it marks the link's action, not its
 * content — the address itself is what the link resolves to, not a line the
 * page repeats.
 */

import { formatDateRange } from "@mhmo91/rx";
import { fromResizeObserver } from "@mhmo91/schmancy/directives";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import { css, html, type PropertyValues } from "lit";
import { property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { asapScheduler, merge, observeOn, Subject, takeUntil } from "rxjs";
import { LocalizeMixin } from "../i18n/localize.ts";
import { EventDescriptionView } from "./event-description.view.ts";

@schmancyElement("event-identity")
export class EventIdentity extends LocalizeMixin(SchmancyElement) {
	/** The one declaration with no utility behind it, measured by A/B:
	 *  `mask-b-from-55%` resolves `var(--tw-mask-linear)`, whose initial value
	 *  comes from an `@property` registration a shadow-adopted sheet cannot
	 *  make, so the utility compiles into the sheet and renders nothing. `#000`
	 *  names no colour the theme could own: a mask reads only the alpha
	 *  channel. An attribute selector rather than a class because the fade is
	 *  conditional on a measured verdict and has to be bindable off. The
	 *  column's containment and the title's ramp are utilities on `class=`
	 *  instead: a rule here would silently outrank any later `text-*` or
	 *  `truncate` on the same element. */
	static styles = [
		css`
			[data-fade] {
				mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
			}
		`,
	];

	@property({ type: String }) name = "";
	/** The event's own description — the buying surface carries one, the ticket
	 *  page's own context has none. */
	@property({ type: String }) description: string | null = null;
	@property({ attribute: false }) start!: Date;
	@property({ attribute: false }) end!: Date;
	/** The venue's zone, never the visitor's — resolved by each host from its
	 *  own catalog read. */
	@property({ type: String }) timezone = "UTC";
	@property({ type: String }) venue: string | null = null;
	/** Absent when the venue resolves to no pin and no labeled address; the
	 *  locator then reads as plain text rather than a link to nowhere. */
	@property({ type: String }) mapsUrl: string | null = null;
	/** Entrance instructions — the ticket templates render no equivalent, so
	 *  order-access is their only home. */
	@property({ type: String }) notes: string | null = null;

	/** Whether each cut actually bit on this instance's own box. Not public
	 *  properties: one line and three lines are intrinsic to the surface, so
	 *  there is no dose for a host to pick. */
	@state() private nameClamped = false;
	@state() private descriptionClamped = false;
	/** The text changing is the one thing a box cannot report, and both runs
	 *  measured here sit at a cap where their own box stops moving. A
	 *  three-line description replaced at one width by a six-line one leaves every
	 *  box byte-identical — measured live: host and block unmoved, scrollHeight
	 *  60 → 100, no resize delivered — and a 25-character name swapped for a
	 *  44-character one is pinned the same way, while both verdicts flip. */
	private readonly textChanged$ = new Subject<void>();

	override connectedCallback() {
		super.connectedCallback();
		// A cut is a fact about a laid-out box, and the box does not exist when
		// the first render returns — the `<schmancy-typography>` host has not
		// upgraded yet and measures 0 in every dimension. It changes again when
		// the webfont swaps in and whenever the column is resized, so the
		// measurement is driven by the box rather than by a render tick.
		//
		// The HOST is what is watched, never either measured run. Each is
		// destroyed and rebuilt the moment its own verdict flips, because its
		// wrapper turns into a <button>; re-attaching an observer to that new
		// node schedules a fresh delivery inside the frame still delivering the
		// one which caused the flip, and the browser reports exactly that as
		// "ResizeObserver loop completed with undelivered notifications". The
		// host outlives every branch.
		merge(fromResizeObserver(this), this.textChanged$)
			// A microtask, so the verdict is never written from inside the update
			// that rendered the text it measures — Lit warns on that, and the
			// warning is right: it costs a second render cycle. Microtask, not
			// timer: it drains before the frame paints, so nothing flashes.
			.pipe(observeOn(asapScheduler), takeUntil(this.disconnecting))
			.subscribe(() => {
				const title =
					this.renderRoot.querySelector<HTMLElement>("[data-title]");
				const block =
					this.renderRoot.querySelector<HTMLElement>("[data-clamp]");
				// A clipped run keeps its full text in scrollWidth/scrollHeight; 1px
				// of slack absorbs sub-pixel advance and line-height rounding, which
				// otherwise reports a run that exactly fits as overflowing.
				this.nameClamped =
					title !== null && title.scrollWidth - title.clientWidth > 1;
				this.descriptionClamped =
					block !== null && block.scrollHeight - block.clientHeight > 1;
			});
	}

	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		if (changed.has("name") || changed.has("description"))
			this.textChanged$.next();
	}

	/** Both doors below open the same overlay on the same facts — a class
	 *  field so each keeps one identity across re-renders, exactly as
	 *  `ticket-type-row`'s `openDescription` does for its own two doors. */
	private readonly openFullEvent = (): void => {
		show(EventDescriptionView, {
			ariaLabel: this.name,
			props: {
				name: this.name,
				description: this.description,
				dateLabel: this.dateline(),
				venue: this.venue,
				mapsUrl: this.mapsUrl,
				notes: this.notes,
			},
		})
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	};

	/** The run in the venue's own zone, read once for the two surfaces that
	 *  say it — this header's line and the overlay it opens — so the reading
	 *  the buyer presses and the reading that opens can never disagree. */
	private dateline(): string {
		const monthKey = (d: Date) =>
			d.toLocaleDateString("en-CA", { timeZone: this.timezone }).slice(0, 7);
		return formatDateRange(this.start, this.end, {
			locale: this.locale,
			month: monthKey(this.start) === monthKey(this.end) ? "long" : "short",
			timeZone: this.timezone,
		});
	}

	override render() {
		// The ramp and the one-line hold ride the typography element itself, so
		// `cqi` resolves against the column and the ellipsis lands in the box the
		// ramp sized. `token=""` is what lets the override land: a rung sets
		// `font-size` from the primitive's own unlayered `:host` rule, which
		// outranks every utility whatever its weight.
		const title = html`
			<schmancy-typography
				data-title
				type="headline"
				token=""
				class="truncate text-[clamp(20px,calc(14.8px_+_3.61cqi),32px)] leading-tight"
			>
				${this.name}
			</schmancy-typography>
		`;

		return html`
			<div class="@container flex flex-col gap-3">
				<div class="flex flex-col gap-1">
					${when(
						this.nameClamped,
						() => html`
							<button
								type="button"
								class="min-w-0 cursor-pointer text-left"
								aria-label=${this.t("storefront.eventIdentity.nameButtonAriaLabel", { name: this.name })}
								@click=${this.openFullEvent}
							>
								${title}
							</button>
						`,
						() => title,
					)}
					<schmancy-typography
						type="label"
						token="sm"
						transform="uppercase"
						class="tracking-widest text-surface-onVariant"
					>
						${this.dateline()}
					</schmancy-typography>
				</div>

				${when(this.description, () => {
					// The cap is unconditional so a description that fits is measured
					// against the same box a description that doesn't is; the mask is not,
					// so a short description renders with nothing dimmed. The cap stays on
					// the typography element itself — `1lh` resolves against that
					// element's own used line-height, which the typescale token sets;
					// on a wrapper it would resolve against preflight's inherited 1.5
					// factor and silently mis-size.
					const body = html`
						<schmancy-typography
							data-clamp
							?data-fade=${this.descriptionClamped}
							type="body"
							token="md"
							class="max-h-[calc(3*1lh)] overflow-hidden"
						>
							${this.description}
						</schmancy-typography>
					`;
					return this.descriptionClamped
						? html`
								<button
									type="button"
									class="max-w-prose cursor-pointer text-left"
									aria-label=${this.t(
										"storefront.eventIdentity.descriptionButtonAriaLabel",
										{
											name: this.name,
										},
									)}
									@click=${this.openFullEvent}
								>
									${body}
								</button>
							`
						: html`<div class="max-w-prose">${body}</div>`;
				})}

				${when(this.venue, () =>
					this.mapsUrl
						? html`
								<a
									href=${this.mapsUrl}
									target="_blank"
									rel="noopener"
									aria-label=${this.t("storefront.eventIdentity.venueMapAriaLabel", { venue: this.venue ?? "" })}
									class="flex w-fit cursor-pointer items-center gap-1"
								>
									<schmancy-icon class="text-sm">directions</schmancy-icon>
									<schmancy-typography type="body" token="sm">${this.venue}</schmancy-typography>
								</a>
							`
						: html`<schmancy-typography type="body" token="sm">${this.venue}</schmancy-typography>`,
				)}

				${when(
					this.notes,
					() => html`
						<schmancy-typography
							type="body"
							token="sm"
							class="text-surface-onVariant"
							aria-label=${this.t("storefront.eventIdentity.entryInfoAriaLabel")}
						>
							${this.notes}
						</schmancy-typography>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"event-identity": EventIdentity;
	}
}
